import { useCallback } from 'react';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { Input } from '@amzn/stencil-react-components/input';

import { RichTextEditorField } from '../RichTextEditorField';
import { RequiredLabel } from './RequiredLabel';

/** The Helios task shape these fields edit (name, description, optional days). */
export interface TaskDetailsValue {
  readonly taskName: string;
  readonly taskDescription: string;
  readonly taskDurationDays?: number | undefined;
}

/** Visible labels/placeholders — supplied by the caller so this stays
 *  i18n-bundle-agnostic and reusable across features. */
export interface TaskDetailsFieldsLabels {
  readonly nameLabel: string;
  readonly namePlaceholder: string;
  readonly descriptionLabel: string;
  readonly descriptionPlaceholder: string;
  readonly durationLabel: string;
  readonly durationPlaceholder: string;
}

export interface TaskDetailsFieldsProps {
  /** Current task value. */
  readonly value: TaskDetailsValue;
  /** Emits the full next task value on any field edit. */
  readonly onChange: (next: TaskDetailsValue) => void;
  readonly labels: TaskDetailsFieldsLabels;
  /**
   * Whether Task Name and Task Description are required (asterisk +
   * `aria-required`). Callers that only render this once a task has been
   * added pass `true`; a surface where the whole task block is optional
   * (e.g. the parent-question Audit Criteria step) passes `false`.
   * Duration is always optional. Defaults to `true`.
   */
  readonly isRequired?: boolean;
  readonly disabled?: boolean;
  readonly dataTestId?: string;
}

/**
 * The editable Task fields — Task name, Duration (days), Task description
 * (rich text) — shared by the audit-criteria step and each sub-question row.
 * Fully controlled: emits the whole next `TaskDetailsValue` on every edit;
 * the caller owns whether a task exists at all (add/remove) and where the
 * value is stored.
 *
 * Duration is digit-filtered and cleared to `undefined` when emptied (so it
 * round-trips under `exactOptionalPropertyTypes`).
 */
export function TaskDetailsFields({
  value,
  onChange,
  labels,
  isRequired = true,
  disabled = false,
  dataTestId,
}: TaskDetailsFieldsProps): JSX.Element {
  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange({ ...value, taskName: e.target.value }),
    [value, onChange],
  );

  const handleDurationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (!/^\d*$/.test(raw)) return; // reject non-digits before state
      // Clearing drops the key (rather than setting undefined) so it
      // round-trips under exactOptionalPropertyTypes.
      onChange(
        raw === ''
          ? { taskName: value.taskName, taskDescription: value.taskDescription }
          : { ...value, taskDurationDays: Number(raw) },
      );
    },
    [value, onChange],
  );

  const handleDescriptionChange = useCallback(
    (html: string) => onChange({ ...value, taskDescription: html }),
    [value, onChange],
  );

  const durationValue =
    value.taskDurationDays !== undefined ? String(value.taskDurationDays) : '';

  return (
    <Col gridGap="dimensions.spacing.300" width="100%">
      <Row gridGap="dimensions.spacing.300" flexWrap="wrap">
        <Col gridGap="dimensions.spacing.100" flex={1} minWidth={0}>
          <RequiredLabel isRequired={isRequired} hasError={false}>
            {labels.nameLabel}
          </RequiredLabel>
          <Input
            value={value.taskName}
            onChange={handleNameChange}
            placeholder={labels.namePlaceholder}
            disabled={disabled}
            aria-required={isRequired}
            data-testid={dataTestId ? `${dataTestId}-taskName` : undefined}
          />
        </Col>
        <Col gridGap="dimensions.spacing.100">
          <Text fontWeight="bold">{labels.durationLabel}</Text>
          <Input
            value={durationValue}
            onChange={handleDurationChange}
            placeholder={labels.durationPlaceholder}
            disabled={disabled}
            data-testid={dataTestId ? `${dataTestId}-taskDuration` : undefined}
          />
        </Col>
      </Row>

      <Col gridGap="dimensions.spacing.100">
        {/* Description is a Stencil RichTextEditor, which only exposes
         *  aria-label(/labelledby) — no aria-required — so the required state
         *  is conveyed by the visible RequiredLabel asterisk only. */}
        <RequiredLabel isRequired={isRequired} hasError={false}>
          {labels.descriptionLabel}
        </RequiredLabel>
        <RichTextEditorField
          value={value.taskDescription}
          onChange={handleDescriptionChange}
          placeholder={labels.descriptionPlaceholder}
          disabled={disabled}
          ariaLabel={labels.descriptionLabel}
          {...(dataTestId !== undefined
            ? { dataTestId: `${dataTestId}-taskDescription` }
            : {})}
        />
      </Col>
    </Col>
  );
}
